// =============================================================================
// OfflineQueueBadge.tsx — "waiting to upload" (10 Oct 2026)
//
// James, 10 Oct 2026, crew on the Nullarbor: "they took a lot yesterday that
// are now missing in ops ... whats our safety net for losing photos?"
//
// Part of the answer was that nobody could SEE the safety net. Photos, forms,
// receipts and diary entries captured without signal sit in a queue on THE
// PHONE (lib/offline) and only upload while Ops is open with signal — and
// nothing on screen said how many were still sitting there.
//
// This is a small pill fixed to the bottom-left of every signed-in screen:
//   * hidden when nothing is waiting
//   * amber "N waiting to upload" while there is
//   * red when a photo's saved copy has gone missing (it can never upload)
// Tap it for the plain-English detail and an "Upload now" button.
//
// Mounted once in App.tsx. Reads lib/offline only — no database, no worker.
//
// 10 Oct 2026 (later, James's phone screenshot): the pill sat ON the phone's
// bottom nav. It now rides above it — the nav's height plus the iPhone home-bar
// inset — and drops to the corner on desktop where there is no bottom nav.
//
// mig 441 — full-size copies still to follow are shown apart, in a quiet grey
// pill: those photos are already in Ops, only the big copy is still here. The
// sheet also says how much of the phone's room for Ops is used (a few hundred
// photos is a lot of megabytes) and goes red past 80%.
//
// 10 Oct 2026 (Al: one pending, "Upload now" does nothing, a black photo) —
// items that cannot upload by themselves are listed with the reason and three
// buttons: Try again · Save a copy (the file, to the phone) · Remove.
// =============================================================================
import { useEffect, useState } from "react";
import { CloudUpload, AlertTriangle, X, RefreshCw, CheckCircle2 } from "lucide-react";
import { queueSummary, onQueueChange, syncNow, dismissLost, storageEstimate, listProblems, retryQueued, removeQueued, getCachedPhoto, type QueueSummary, type QueueProblem } from "@/lib/offline";

const EMPTY: QueueSummary = { waiting: 0, stuck: 0, full: 0, photos: 0, lost: 0, oldest: null, lastError: null };

async function saveCopy(p: QueueProblem) {
  if (!p.local_id) return;
  const c = await getCachedPhoto(p.local_id).catch(() => null);
  if (!c?.blob) { window.alert("There is no file saved on this phone for this one."); return; }
  const url = URL.createObjectURL(c.blob);
  const a = document.createElement("a");
  a.href = url; a.download = `ops-${p.id}.jpg`; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function sinceText(iso: string | null): string {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs} hour${hrs === 1 ? "" : "s"}`;
  return `${Math.round(hrs / 24)} days`;
}

export default function OfflineQueueBadge() {
  const [sum, setSum] = useState<QueueSummary>(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [space, setSpace] = useState<{ usedMb: number; quotaMb: number; pct: number } | null>(null);
  const [problems, setProblems] = useState<QueueProblem[]>([]);

  useEffect(() => {
    let alive = true;
    const read = () => {
      queueSummary().then(s => { if (alive) setSum(s); }).catch(() => {});
      storageEstimate().then(e => { if (alive) setSpace(e); }).catch(() => {});
      listProblems().then(p => { if (alive) setProblems(p); }).catch(() => {});
    };
    read();
    const off = onQueueChange(read);
    const t = setInterval(read, 10_000);
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { alive = false; off(); clearInterval(t); window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  if (sum.waiting === 0 && sum.lost === 0 && sum.full === 0 && sum.stuck === 0) return null;

  const other = sum.waiting - sum.photos;
  const spaceTight = !!space && space.pct >= 80;
  const red = sum.lost > 0 || spaceTight || sum.stuck > 0;
  const quiet = !red && sum.waiting === 0;      // only full-size copies left
  const label = sum.waiting > 0
    ? `${sum.waiting} waiting to upload`
    : sum.stuck > 0 ? `${sum.stuck} can't upload — tap`
    : sum.lost > 0 ? `${sum.lost} photo${sum.lost === 1 ? "" : "s"} lost`
    : `${sum.full} full-size to follow`;

  async function go() {
    setBusy(true);
    try { await syncNow(); } finally { setBusy(false); }
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className={`fixed left-3 bottom-[calc(env(safe-area-inset-bottom,0px)+5.5rem)] md:bottom-6 z-30 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium shadow-lg ${
          red ? "bg-danger text-white border-danger" : quiet ? "bg-ink-card text-text-muted border-line" : "bg-warn text-black border-warn"}`}>
        {red ? <AlertTriangle size={13} /> : <CloudUpload size={13} />}
        {label}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center md:justify-center" onClick={() => setOpen(false)}>
          <div className="bg-ink-card border-t md:border border-line w-full md:max-w-md md:rounded-lg p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div className="text-[16px] font-semibold">Waiting to upload</div>
              <button type="button" onClick={() => setOpen(false)} className="text-text-muted hover:text-text p-2.5 -m-1.5 rounded-md"><X size={18} /></button>
            </div>

            {sum.waiting > 0 ? (
              <>
                <div className="mt-3 text-[28px] font-semibold tracking-tight text-warn">{sum.waiting}</div>
                <div className="text-[13px] text-text">
                  {sum.photos > 0 && <>{sum.photos} HOP photo{sum.photos === 1 ? "" : "s"}</>}
                  {sum.photos > 0 && other > 0 && " and "}
                  {other > 0 && <>{other} other item{other === 1 ? "" : "s"} (forms, receipts, diary, timesheets)</>}
                  {" "}saved on <span className="font-semibold">this phone</span>, not in Ops yet.
                  {sum.oldest && <> The oldest has been waiting {sinceText(sum.oldest)}.</>}
                </div>
                <div className="mt-3 text-[12px] text-text-muted space-y-1.5">
                  <div>They upload by themselves while Ops is <span className="text-text">open on this phone</span> and there is signal. Ops cannot upload while it is closed or the screen is locked.</div>
                  <div>Back in signal: open Ops, leave it on the screen, and wait for this number to reach 0.</div>
                  <div>Do not clear the browser's data or remove Ops from this phone until it does.</div>
                </div>
                {sum.lastError && (
                  <div className="mt-3 text-[11px] font-mono text-text-dim break-words">Last try: {sum.lastError}</div>
                )}
              </>
            ) : (
              sum.stuck === 0 && <div className="mt-3 text-[13px] text-text inline-flex items-center gap-2"><CheckCircle2 size={15} className="text-ok" /> Every photo and form is in Ops.</div>
            )}

            {problems.length > 0 && (
              <div className="mt-4 bg-danger/10 border border-danger/30 rounded-md p-3">
                <div className="text-[13px] text-danger font-medium">{problems.length} {problems.length === 1 ? "has" : "have"} not gone up</div>
                <div className="text-[12px] text-text-muted mt-1">Ops has tried and {problems.length === 1 ? "it keeps" : "they keep"} failing. Nothing else is held up by {problems.length === 1 ? "it" : "them"}.</div>
                <div className="mt-2 space-y-2">
                  {problems.map(p => (
                    <div key={p.id} className="rounded border border-line bg-ink-card p-2">
                      <div className="text-[12px] text-text">{p.label}</div>
                      <div className="text-[11px] text-text-muted mt-0.5">{p.reason}</div>
                      <div className="mt-1.5 flex items-center gap-3 text-[12px]">
                        <button type="button" className="text-accent" onClick={() => retryQueued(p.id)}>Try again</button>
                        {p.local_id && <button type="button" className="text-text-muted underline" onClick={() => saveCopy(p)}>Save a copy</button>}
                        <button type="button" className="text-danger ml-auto"
                          onClick={() => { if (window.confirm("Remove this from the phone? It will NOT be uploaded. If it was a photo you need, take it again.")) void removeQueued(p.id); }}>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sum.full > 0 && (
              <div className="mt-3 rounded-md border border-line bg-ink-raised p-3 text-[12px] text-text-muted">
                <span className="text-text font-medium">{sum.full} full-size cop{sum.full === 1 ? "y" : "ies"} still to follow.</span>{" "}
                Those photos are already in Ops at the size the client asks for. The big copies (kept for our own review) go up after everything else, whenever Ops is open with signal — best left on charge at the roadhouse.
              </div>
            )}

            {space && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] text-text-muted">
                  <span>Room on this phone for Ops</span>
                  <span className={spaceTight ? "text-danger font-medium" : ""}>{space.usedMb >= 1024 ? `${(space.usedMb / 1024).toFixed(1)} GB` : `${space.usedMb} MB`} of {(space.quotaMb / 1024).toFixed(1)} GB used</span>
                </div>
                <div className="mt-1 h-1.5 rounded bg-ink-raised overflow-hidden">
                  <div className={`h-full ${spaceTight ? "bg-danger" : "bg-accent"}`} style={{ width: `${Math.max(2, Math.min(100, space.pct))}%` }} />
                </div>
                {spaceTight && <div className="mt-1.5 text-[12px] text-danger">Nearly full. Get to signal and let Ops upload before taking many more — a photo that cannot be saved here shows as NOT SAVED.</div>}
              </div>
            )}

            {sum.lost > 0 && (
              <div className="mt-4 bg-danger/10 border border-danger/30 rounded-md p-3">
                <div className="text-[13px] text-danger font-medium">{sum.lost} photo{sum.lost === 1 ? "" : "s"} could not be recovered</div>
                <div className="text-[12px] text-text-muted mt-1">
                  The copy saved on this phone is gone (the phone cleared it, or its storage was full). {sum.lost === 1 ? "It" : "They"} will need to be taken again. Tell the office which site.
                </div>
                <button type="button" onClick={() => dismissLost()} className="mt-2 text-[12px] text-text-muted underline">OK, clear this message</button>
              </div>
            )}

            <div className="mt-5 flex items-center justify-between gap-3">
              <span className={`text-[12px] ${online ? "text-ok" : "text-danger"}`}>{online ? "Phone says it has signal" : "No signal"}</span>
              {(sum.waiting > 0 || sum.full > 0) && (
                <button type="button" onClick={go} disabled={busy || !online}
                  className="inline-flex items-center gap-1.5 rounded-md bg-accent text-white px-3.5 py-2 text-[13px] font-medium disabled:opacity-50">
                  <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Uploading…" : "Upload now"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
